import { useEffect, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from 'react-query';
import { Helmet } from 'react-helmet-async';
import { t } from 'i18next';
import { ArrowLeft, BellFill, LikeFill, Plus, ProfileFill } from '@peacecover/icon';
import useChatRoomsQuery from '../../components/api/queries/useChatRoomsQuery';
import { useGetBettieBalances } from '@pocapan/shared/domains/bettie/ui/hooks/useGetBettieBalances';
import { useHandleHistoryBack } from '@pocapan/shared/utils/useHandleHistoryBack';
import BettieChargeBottomSheet from '../../components/bottomSheet/bettie/BettieChargeBottomSheet';
import BettieCreateRoomTicketBottomSheet from '../../components/bottomSheet/bettie/BettieCreateRoomTicketBottomSheet';
import BettieChatCard from './components/BettieChatCard';
import PopUpBasic from '../components/popup/PopUpBasic';
import BettieFreeChargeSection from './components/BettieFreeChargeSection';
import BettieUnfinishedChatRoomCard from './components/BettieUnfinishedChatRoomCard';
import HomeBottomNav from '../../layout/HomeBottomNav';
import { sortChatRoomsByRecentMessage } from './utils/sortChatRoomsByRecentMessage';
import useUserQuery from '../../components/api/queries/useUserQuery';
import { useUnreadMessageCount } from '../my-page/hooks/useUnreadMessageCount';

interface BettieChatListPageProps {
  showBackButton?: boolean;
  showBottomNav?: boolean;
  showHeaderShortcutButtons?: boolean;
  showPurchaseHistoryButton?: boolean;
}

const BettieChatListPage = ({
  showBackButton = true,
  showBottomNav = false,
  showHeaderShortcutButtons = false
}: BettieChatListPageProps) => {
  const handleHistoryBack = useHandleHistoryBack();
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const user = useUserQuery();
  const unreadMessageCount = useUnreadMessageCount();
  const [isCreateTicketBottomSheetOpen, setIsCreateTicketBottomSheetOpen] = useState(false);
  const [isChargeBottomSheetOpen, setIsChargeBottomSheetOpen] = useState(false);
  const [isUnfinishedPopupOpen, setIsUnfinishedPopupOpen] = useState(false);

  const { data, isFetching: isChatRoomsLoading } = useChatRoomsQuery(
    {
      type: 'All',
      tradingPhotoCardId: undefined,
      onlyJoined: true,
      onlyBettie: true
    },
    {
      refetchOnWindowFocus: false
    }
  );
  const { data: bettieBalances, isLoading: isBettieBalancesLoading } = useGetBettieBalances();

  const bettieChatRooms = useMemo(
    () => sortChatRoomsByRecentMessage(data?.pages?.flatMap((page) => page.chatRooms) ?? []),
    [data]
  );
  const [firstChatRoom, ...remainingChatRooms] = bettieChatRooms;
  const bettieHeartCount = bettieBalances?.bettiePoint?.balance ?? 0;

  // 결제 성공 메시지 수신 시 베티 잔액 관련 쿼리 무효화
  useEffect(() => {
    const handleMessage = (event: MessageEvent) => {
      if (event.origin !== window.location.origin) return;
      const payload = event.data as { type?: string; feature?: string } | undefined;
      if (payload?.type === 'PAYMENT_SUCCESS') {
        queryClient.invalidateQueries(['bettieBalances']);
        queryClient.invalidateQueries(['mySubscription']);
      }
    };
    window.addEventListener('message', handleMessage);
    return () => {
      window.removeEventListener('message', handleMessage);
    };
  }, [queryClient]);

  const hasBettieChatRoomTicket =
    bettieBalances?.bettieChatRoomTicket?.balance &&
    bettieBalances?.bettieChatRoomTicket?.balance > 0;

  const handleAddChatRoomTicket = () => {
    if (!bettieBalances?.bettieChatRoomTicket) {
      return;
    }
    if (hasBettieChatRoomTicket) {
      setIsUnfinishedPopupOpen(true);
    } else {
      setIsCreateTicketBottomSheetOpen(true);
    }
  };

  return (
    <div className="min-h-screen bg-background-light pb-[calc(env(safe-area-inset-bottom)+80px)] dark:bg-background-dark">
      <Helmet>
        <title>Bettie</title>
      </Helmet>
      <BettieHomeHeader
        showBackButton={showBackButton}
        showShortcutButtons={showHeaderShortcutButtons}
        heartCount={bettieHeartCount}
        unreadMessageCount={unreadMessageCount}
        profileThumbnailUrl={
          user?.baseUserProfile?.thumbnailUrl || user?.baseUserProfile?.pictureUrl
        }
        onClickBackButton={handleHistoryBack}
        onClickHeart={() => setIsChargeBottomSheetOpen(true)}
        onClickNotifications={() => navigate('/notifications')}
        onClickProfile={() => navigate('/my')}
      />
      <main className="px-[16px]">
        <div className="flex flex-col gap-[10px]">
          {isChatRoomsLoading ? (
            Array.from({ length: 2 }).map((_, index) => (
              // biome-ignore lint/suspicious/noArrayIndexKey: skeleton key
              <BettieChatRoomCardSkeleton key={index} />
            ))
          ) : (
            <>
              {firstChatRoom && <BettieChatCard chatRoom={firstChatRoom} />}
              <BettieFreeChargeSection />
              {remainingChatRooms.map((chatRoom) => (
                <BettieChatCard key={chatRoom.chatRoomId} chatRoom={chatRoom} />
              ))}
              {!isBettieBalancesLoading && (
                <button
                  type="button"
                  onClick={handleAddChatRoomTicket}
                  className="flex h-[109px] w-full flex-col items-center justify-center rounded-[20px] bg-pure-white text-pure-black shadow-[0_0_3px_rgba(0,0,0,0.02),_0_0_13px_rgba(0,0,0,0.03)] dark:bg-background-elevated dark:text-text-main-lv1"
                >
                  <Plus size={21} className="mb-[4px]" />
                  <span className="text-[16px] font-normal leading-[19px]">
                    {t('BettieChatList.startNewChat')}
                  </span>
                </button>
              )}
              {isBettieBalancesLoading ||
              bettieBalances?.bettieChatRoomTicket?.balance === 0 ? null : (
                <BettieUnfinishedChatRoomCard
                  balance={bettieBalances?.bettieChatRoomTicket?.balance ?? 0}
                />
              )}
            </>
          )}
        </div>
        <p className="mx-auto mt-[14px] w-[262px] whitespace-pre-line text-center text-[12px] font-medium leading-[14px] text-text-sub-lv3">
          {t('BettieSetting.bettieDescription')}
        </p>
      </main>
      <BettieCreateRoomTicketBottomSheet
        isOpen={isCreateTicketBottomSheetOpen}
        onClose={() => setIsCreateTicketBottomSheetOpen(false)}
      />
      <BettieChargeBottomSheet
        isOpen={isChargeBottomSheetOpen}
        onClose={() => setIsChargeBottomSheetOpen(false)}
        preferOnetimeWhenHasAutoTopup={true}
      />
      {isUnfinishedPopupOpen && (
        <UnfinishedPopup setIsUnfinishedPopupOpen={setIsUnfinishedPopupOpen} />
      )}
      {showBottomNav && <HomeBottomNav tabIndex={0} />}
    </div>
  );
};

export default BettieChatListPage;

const BettieHomeHeader = ({
  showBackButton,
  showShortcutButtons,
  heartCount,
  unreadMessageCount,
  profileThumbnailUrl,
  onClickBackButton,
  onClickHeart,
  onClickNotifications,
  onClickProfile
}: {
  showBackButton: boolean;
  showShortcutButtons: boolean;
  heartCount: number;
  unreadMessageCount: number;
  profileThumbnailUrl?: string | null;
  onClickBackButton: () => void;
  onClickHeart: () => void;
  onClickNotifications: () => void;
  onClickProfile: () => void;
}) => {
  const formattedHeartCount = formatBadgeCount(heartCount);
  const formattedUnreadCount = formatBadgeCount(unreadMessageCount);

  return (
    <header className="flex items-center justify-between px-[20px] pb-[49px] pt-[calc(32px+env(safe-area-inset-top))]">
      {showBackButton ? (
        <button
          type="button"
          aria-label="Back"
          onClick={onClickBackButton}
          className="grid size-[46px] place-items-center"
        >
          <ArrowLeft size={24} className="text-text-main-lv1" />
        </button>
      ) : (
        <BettieLogoStack />
      )}
      <div className="flex items-center gap-[3px]">
        <HeaderActionButton ariaLabel={t('BettieChatList.purchase')} onClick={onClickHeart}>
          <LikeFill
            size={26}
            gradientStops={[
              { offset: '0%', color: '#FF45A9' },
              { offset: '100%', color: '#FF6A6A' }
            ]}
          />
          {heartCount > 0 && <HeaderBadge>{formattedHeartCount}</HeaderBadge>}
        </HeaderActionButton>
        {showShortcutButtons && (
          <>
            <HeaderActionButton ariaLabel={t('tabs.notifications')} onClick={onClickNotifications}>
              <BellFill size={26} className="text-pure-black dark:text-text-main-lv1" />
              {unreadMessageCount > 0 && <HeaderBadge>{formattedUnreadCount}</HeaderBadge>}
            </HeaderActionButton>
            <HeaderActionButton ariaLabel={t('tabs.profile')} onClick={onClickProfile}>
              {profileThumbnailUrl ? (
                <img
                  src={profileThumbnailUrl}
                  alt=""
                  className="size-[26px] rounded-full object-cover"
                />
              ) : (
                <ProfileFill size={26} className="text-theirty-light dark:text-theirty-dark" />
              )}
            </HeaderActionButton>
          </>
        )}
      </div>
    </header>
  );
};

const BettieLogoStack = () => {
  const logoMaskStyle = {
    WebkitMaskImage: "url('/assets/bettie/logo_bettie.svg')",
    maskImage: "url('/assets/bettie/logo_bettie.svg')",
    WebkitMaskRepeat: 'no-repeat',
    maskRepeat: 'no-repeat',
    WebkitMaskSize: '100% 100%',
    maskSize: '100% 100%'
  };
  return (
    <div
      role="img"
      aria-label="Bettie"
      className="grid h-[28px] w-[95px] grid-cols-[max-content] grid-rows-[max-content] place-items-start"
    >
      <span
        aria-hidden="true"
        className="relative col-start-1 row-start-1 h-[28px] w-[95px] blur-[15px]"
      >
        <span className="bettie-logo-angular-glow absolute inset-0" style={logoMaskStyle} />
      </span>
      <img
        src="/assets/bettie/logo_bettie.svg"
        alt=""
        aria-hidden="true"
        className="col-start-1 row-start-1 h-[28px] w-[95px] opacity-20 blur-[2px]"
      />
      <span
        aria-hidden="true"
        className="col-start-1 row-start-1 h-[28px] w-[95px] bg-pure-white"
        style={logoMaskStyle}
      />
    </div>
  );
};

const HeaderActionButton = ({
  ariaLabel,
  children,
  onClick
}: {
  ariaLabel: string;
  children: ReactNode;
  onClick: () => void;
}) => (
  <button
    type="button"
    aria-label={ariaLabel}
    onClick={onClick}
    className="relative grid size-[46px] place-items-center rounded-full bg-pure-white shadow-[0_8px_24px_rgba(0,0,0,0.08)] dark:bg-background-elevated"
  >
    {children}
  </button>
);

const HeaderBadge = ({ children }: { children: ReactNode }) => (
  <span className="absolute right-[3px] top-0 flex h-[13px] min-w-[13px] items-center justify-center rounded-[42px] bg-primary-light px-[4px] text-[9px] font-semibold leading-[11px] text-pure-white dark:bg-primary-dark">
    {children}
  </span>
);

const formatBadgeCount = (count: number) => {
  if (count > 99) return '99+';
  return String(count);
};

const BettieChatRoomCardSkeleton = () => {
  return (
    <div className="w-full animate-pulse rounded-[20px] bg-pure-white px-[18px] py-[8px] shadow-[0_0_3px_rgba(0,0,0,0.02),_0_0_13px_rgba(0,0,0,0.03)] dark:bg-background-elevated">
      <div className="flex items-center justify-between border-b border-surface-lv1 py-[10px]">
        <div className="h-[14px] w-[70px] rounded-full bg-gray-100 dark:bg-gray-800" />
        <div className="h-[14px] w-[90px] rounded-full bg-gray-100 dark:bg-gray-800" />
      </div>
      <div className="flex items-center gap-[10px] py-[12px]">
        <div className="size-[48px] rounded-full bg-gray-100 dark:bg-gray-800" />

        <div className="flex flex-1 flex-col items-start gap-[4px]">
          <div className="flex w-full items-center gap-[4px]">
            <div className="h-[18px] w-[80px] rounded-full bg-gray-100 dark:bg-gray-800" />
            <div className="h-[14px] w-[14px] rounded-full bg-gray-100 dark:bg-gray-800" />
          </div>
          <div className="h-[16px] w-[70%] rounded-full bg-gray-100 dark:bg-gray-800" />
        </div>
      </div>
    </div>
  );
};

const UnfinishedPopup = ({
  setIsUnfinishedPopupOpen
}: {
  setIsUnfinishedPopupOpen: (isOpen: boolean) => void;
}) => {
  const navigate = useNavigate();
  return (
    <div className="fixed top-0 left-0 w-full h-full bg-black/50 z-[999] flex items-center justify-center">
      <PopUpBasic
        type="short"
        title={t('BettieChatList.haveUnfinishedChatRoom')}
        description={t('BettieChatList.selectAndChat')}
        primaryButtonText={t('no')}
        onPrimaryButtonClick={() => setIsUnfinishedPopupOpen(false)}
        secondaryButtonText={t('yes')}
        onSecondaryButtonClick={() => {
          setIsUnfinishedPopupOpen(false);
          navigate('/bettie-setting', { state: { initialStep: 'room_intro' } });
        }}
        className="w-[311px]"
      />
    </div>
  );
};
